import { useCallback, useEffect, useState } from "react";

import { CalendarIcon, ClipboardCheck, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch.jsx";
import { Label } from "../../components/ui/label.jsx";

import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useConfirm } from "../../context/ConfirmContext.jsx";
import { usePagination } from "../../hooks/usePagination";

import AttendanceDialog from "./component/AttendanceDialog";
import AttendanceTable from "./component/AttendanceTable";

const Attendance = () => {
  const { user } = useAuth();
  const api = useApi();
  const [showDeleted, setShowDeleted] = useState(false);

  const [todayAttendance, setTodayAttendance] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState("ALL");
  const [selectedType, setSelectedType] = useState("ALL");
  const [selectedProject, setSelectedProject] = useState("ALL");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [direction, setDirection] = useState("desc");

  const pagination = usePagination(10);
  const { items: attendance, currentPage: page, pageSize, setPaginationData, setCurrentPage } = pagination;

  const onSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(0); // Reset to first page on search
  };

  const confirm = useConfirm();

  // Delete attendance record
  const deleteAttendance = async (id) => {
    const isConfirmed = await confirm({
      title: "Delete Attendance Record",
      message: "Are you sure you want to delete this attendance record?"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/attendance/${id}`);
      toast.success("Attendance record deleted");
      loadAttendance();
    } catch (error) {
      toast.error(error.message || "Failed to delete attendance record");
    }
  };

  // Load attendance records for selected date range
  const loadAttendance = useCallback(async () => {
    try {
      const start = startDate || new Date();
      const end = endDate || new Date();
      const startStr = new Date(start).toLocaleDateString("en-CA");
      const endStr = new Date(end).toLocaleDateString("en-CA");

      const params = {
        start: startStr,
        end: endStr,
        page,
        size: pageSize,
        sortBy: sortBy || undefined,
        direction: direction || undefined,
        showDeleted
      };

      if (selectedUser !== "ALL") params.userId = selectedUser;
      if (selectedProject !== "ALL") params.projectId = selectedProject;
      if (selectedType !== "ALL") params.type = selectedType;
      if (searchTerm) params.search = searchTerm;

      const todayParams = {};
      if (selectedProject !== "ALL") todayParams.projectId = selectedProject;

      const [allResponse, todayResponse] = await Promise.all([
        api.get("/attendance", { params }),
        api.get("/attendance/today", { params: todayParams })
      ]);

      setPaginationData(allResponse.data);
      setTodayAttendance(todayResponse.data);
    } catch (error) {
      console.error("Failed to load attendance:", error);
      toast.error(error.message || "Failed to load attendance");
    }
  }, [
    api,
    startDate,
    endDate,
    selectedUser,
    selectedType,
    selectedProject,
    page,
    pageSize,
    searchTerm,
    sortBy,
    direction,
    showDeleted,
    setPaginationData
  ]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 space-y-6">
        <PageHeader
          title="Attendance Tracking"
          subtitle="Monitor worker attendance and hours"
          actionLabel={user?.permissions?.attendance?.create ? "Record Attendance" : null}
          onActionClick={() => setIsCreateOpen(true)}
          icon={UserPlus}
          pageIcon={ClipboardCheck}
          extraActions={
            <div className="flex items-center gap-4">
              <SearchInput placeholder="Search names..." onSearch={onSearch} value={searchTerm} className="w-full md:w-80" />
            </div>
          }
        />

        <AttendanceDialog open={isCreateOpen} onOpenChange={setIsCreateOpen} onSuccess={loadAttendance} />

        {/* Always show attendance summary and records, no project filter */}
        <div className="space-y-6">
          {/* Today's Attendance Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-yale-blue-500" />
                Today's Attendance Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">
                    {todayAttendance.filter((a) => a.status.toLowerCase() === "present").length}
                  </p>
                  <p className="text-sm text-slate-600">Present</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-600">
                    {todayAttendance.filter((a) => a.status.toLowerCase() === "absent").length}
                  </p>
                  <p className="text-sm text-slate-600">Absent</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-yellow-600">
                    {todayAttendance.filter((a) => a.status.toLowerCase() === "late").length}
                  </p>
                  <p className="text-sm text-slate-600">Late</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-yale-blue-600">
                    {todayAttendance.filter((a) => a.status.toLowerCase() === "half_day").length}
                  </p>
                  <p className="text-sm text-slate-600">Half Day</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <div className="flex items-center gap-2 px-2">
              <Users className="w-5 h-5 text-purple-500" />
              <h2 className="text-lg font-semibold text-yale-blue-900 dark:text-slate-100">Attendance Records</h2>
            </div>

             <AttendanceTable
              attendance={attendance}
              filters={{
                selectedUser,
                selectedType,
                selectedProject,
                dateRange: { start: startDate, end: endDate }
              }}
              onFilterChange={(type, value) => {
                if (type === "user") setSelectedUser(value);
                if (type === "type") setSelectedType(value);
                if (type === "project") setSelectedProject(value);
                if (type === "dateRange") {
                  setStartDate(value.start);
                  setEndDate(value.end);
                }
                setCurrentPage(0);
              }}
              permissions={{
                canDelete: user?.permissions?.attendance?.delete
              }}
              onDelete={deleteAttendance}
              sortBy={sortBy}
              direction={direction}
              onSort={(key, dir) => {
                setSortBy(key);
                setDirection(dir);
              }}
              showDeleted={showDeleted}
              setShowDeleted={setShowDeleted}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <Pagination pagination={pagination} />
      </div>
    </div>
  );
};

export default Attendance;
